/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useDispatch, useSelector } from "react-redux";
import {
    Nav, NavItem, NavLink,
} from "reactstrap";

import classnames from "classnames";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router";
import Select from "react-select";

import { useNavigate } from "react-router-dom";
import { assignDeviceToTenant, getDevice, updateDevice } from "../../../actions/Devices/actions";
import { listTenants } from "../../../actions/Tenants/actions";
import { CONFIG_TYPES } from "../../../actions/Tenants/config/constants";
import { AvosAction } from "../../../actions/types";
import { useTenantActiveConfigs } from "../../../hooks/useTenantConfigQueries";
import miniIcon from "../../../img/Mini.png";
import DeviceLastSeen from "../../Devices/DeviceLastSeen";
import DeviceTenantDropdown from "../../Devices/DeviceTenantDropdown";
import { FormContainer } from "../../Helper/FormContainer";
import { Loading } from "../../Helper/Loading";
import FullPageLayout from "../Layout/FullPageLayout";
import { DeviceAdminCommand, DeviceAdminCommandSections } from "./components/DeviceAdminCommands";
import DeviceEventsHistory from "./components/DeviceEventsHistory";
import { DeviceStatusTable } from "./components/DeviceStatusTable";
import { DeviceUpdateOptionTable } from "./components/DeviceUpdateOptionTable";
import { FtaControls } from "./components/FtaControls";
import GatewayStatusTab from "./components/GatewayStatusTab";
import RegisterConfigTab from "./components/RegisterConfigTab";
import RipeningProgramsTab from "./components/RipeningProgramsTab";
import { RipetapControls } from "./components/RipetapControls";
import RipetapScheduleSettings from "./components/RipetapScheduleSettings";

interface DeviceTab {
    name: string;
    key: string;
    tab: (props?: any) => JSX.Element;
    deviceTypes?: string[];
    excludeDeviceTypes?: string[];
    adminOnly?: boolean;
}

const tablist: DeviceTab[] = [
    { name: "Device status logs", key: "status", tab: (props) => <DeviceStatusTable {...props} />, excludeDeviceTypes: ["ripening_room_gateway"] }, // eslint-disable-line react/display-name
    { name: "Power Options", key: "power", tab: (props) => <DeviceAdminCommand type={DeviceAdminCommandSections.POWER} {...props} />, deviceTypes: ["mini"] }, // eslint-disable-line react/display-name
    { name: "Update options", key: "update", tab: (props) => <DeviceUpdateOptionTable type={DeviceAdminCommandSections.POWER} {...props} />, deviceTypes: ["mini"] }, // eslint-disable-line react/display-name
    { name: "Test device", key: "test", tab: (props) => <DeviceAdminCommand type={DeviceAdminCommandSections.TEST} {...props} />, deviceTypes: ["mini"] }, // eslint-disable-line react/display-name
    { name: "RipeTap Controls", key: "ripetap-controls", tab: (props) => <RipetapControls {...props} />, deviceTypes: ["ripetap"] }, // eslint-disable-line react/display-name
    { name: "Ripetap Schedule", key: "ripetap", tab: (props) => <RipetapScheduleSettings {...props} />, deviceTypes: ["ripetap"] }, // eslint-disable-line react/display-name
    { name: "FTA Controls", key: "fta-controls", tab: (props) => <FtaControls {...props} />, deviceTypes: ["fta"] }, // eslint-disable-line react/display-name
    { name: "Gateway Status", key: "gateway-status", tab: (props) => <GatewayStatusTab {...props} />, deviceTypes: ["ripening_room_gateway"] }, // eslint-disable-line react/display-name
    { name: "Register Configuration", key: "registers", tab: (props) => <RegisterConfigTab {...props} />, deviceTypes: ["ripening_room_gateway"] }, // eslint-disable-line react/display-name
    { name: "Ripening Programs", key: "ripening-programs", tab: (props) => <RipeningProgramsTab {...props} />, deviceTypes: ["ripening_room_gateway"], adminOnly: true }, // eslint-disable-line react/display-name
    { name: "Event History", key: "event", tab: (props) => <DeviceEventsHistory {...props} />, excludeDeviceTypes: ["ripening_room_gateway"] }, // eslint-disable-line react/display-name
];

export default function DeviceManagement() {
    const device = useSelector((state: any) => state.devices.current);
    const isAdmin = useSelector((state: any) => state.auth.user?.is_admin);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const activeTab = params.view || tablist[0].key;
    // * The flag of the tenant the device belongs to (not the viewer's tenant); same query as the Ripening Programs tab.
    const deviceTenantConfigs = useTenantActiveConfigs(device?.type === "ripening_room_gateway" ? device?.tenant_id : undefined);
    const ripewiseEnabled = !!(deviceTenantConfigs.data
        ?.find((item) => item.type === CONFIG_TYPES.ROOT && item.value === "root")?.config as Record<string, any> | undefined)?.ripening_room_enable_ripewise;

    useEffect(() => {
        dispatch(listTenants() as AvosAction);
    }, []);

    useEffect(() => {
        if (device?.UID !== params.device_uid) {
            dispatch(getDevice(params.device_uid) as AvosAction);
        }
    }, [params.device_uid, device?.UID]);

    // Filter tabs based on device type
    const filteredTabs = useMemo(() => {
        if (!device?.type) return tablist;

        return tablist.filter((tab) => {
            if (tab.adminOnly && !isAdmin) return false;
            if (tab.key === "ripening-programs" && !ripewiseEnabled) return false;
            if (tab.excludeDeviceTypes?.includes(device.type)) return false;
            return !tab.deviceTypes || tab.deviceTypes.includes(device.type);
        });
    }, [device?.type, isAdmin, ripewiseEnabled]);

    const currentTabContent = useMemo(() => {
        const tab = filteredTabs.find((i) => i.key === activeTab);
        return tab ? tab.tab({}) : filteredTabs[0]?.tab({});
    }, [activeTab, filteredTabs]);

    if (!device || !device.UID || String(device?.UID) !== params.device_uid) {
        return <div css={css`min-height: 100vh;`}><Loading /></div>;
    }

    const joinStatus = [
        { value: "on_hold", label: "On hold" },
        { value: "joining", label: "Joining" },
        { value: "joined", label: "Joined" },
        { value: "declined", label: "Declined" }
    ];
    // TODO implement update system to the backend, like the thing done here above

    const buttons = (
        <div className="d-flex text-start justify-content-end">
            <div css={css`width: 20rem;`}>
                <DeviceTenantDropdown
                    device={device}
                    onChange={(tenant_id) => dispatch(assignDeviceToTenant({
                        ...device,
                        tenant_id,
                        tenant: tenant_id
                    }) as AvosAction)}
                />
            </div>

            <div css={css`width: 20rem;`} className="ms-2">
                <Select
                    options={joinStatus}
                    name="action"
                    value={joinStatus.find((x) => x.value === device.joinstatus)}
                    onChange={(joinStatus_item) => dispatch(updateDevice({
                        ...device,
                        joinstatus: joinStatus_item?.value
                    }) as AvosAction)}
                />
            </div>

            <a
                className="btn btn-primary ms-2 text-nowrap"
                href={`https://experiencedata.grafana.net/explore?orgId=1&left=[%22now-1h%22,%22now%22,%22grafanacloud-experiencedata-logs%22,%7B%22expr%22:%22%7Binstance%3D%5C%22${device.name}%5C%22%7D%22%7D]`}
            >
                Grafana Logs
            </a>
        </div>
    );

    return (
        <FullPageLayout>
            <FormContainer fullWidth actionsTop={buttons}>
                <div className="d-flex align-items-center gap-4">
                    <img src={miniIcon} alt="Meneer AVOS" />

                    <div>
                        <h3>{device.name}</h3>
                        <h6>UUID: {device.UID} </h6>
                        <h6 className="text-muted"><DeviceLastSeen device={device} refresh={true} /> </h6>
                    </div>
                </div>

                <div className="d-flex flex-column gap-3">
                    <Nav tabs>
                        {filteredTabs.map((tab) => (
                            <NavItem key={tab.key} >
                                <NavLink
                                    className={classnames({ active: activeTab === tab.key })}
                                    onClick={() => navigate(`/device-management/${params.device_uid}/view/${tab.key}`)}
                                >
                                    {tab.name}
                                </NavLink>
                            </NavItem>
                        ))}
                    </Nav>

                    {currentTabContent}
                </div>
            </FormContainer>
        </FullPageLayout>
    );
}
